import type { PointOptionsObject } from "highcharts";

import type {
    MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode,
    MarketResearchDemandAnalysisCommonModelsChangeGrowth,
    MarketResearchDemandAnalysisCalculationModelsNodeTrendPeriodData,
} from "@similarweb/pro-api-client";

import { CHART_COLORS } from "app/constants/ChartColors";
import { createKeywordListEntity } from "app/modules/demand-analysis/keyword-lists";

import type { CompareMetricResponse } from "../search-trends-page/common/types";
import { type IRawDataMetrics, ITotalScoreTypes } from "../search-trends-page/demand-size/types";

import type { IChartTooltipDataItem, IChartTooltipDataItemRow } from "./types";

declare module "highcharts" {
    interface PointOptionsObject {
        date?: string;
        isMonthToDate?: boolean;
    }
}

export interface ILineChartSeriesData {
    id: string;
    name: string;
    isPeriodOverPeriod?: boolean;
    data: PointOptionsObject[];
    isPreviousPeriod?: boolean;
    monthToDateIndex?: number;
}

type TransformedData = {
    totalData: IRawDataMetrics | CompareMetricResponse;
    chartData: ILineChartSeriesData[];
    categories: string[];
    chartTooltipData: IChartTooltipDataItem[];
};

const growthToTotalScoreMap: Record<MarketResearchDemandAnalysisCommonModelsChangeGrowth, ITotalScoreTypes> = {
    Growing: ITotalScoreTypes.GROWING,
    Declining: ITotalScoreTypes.DECLINING,
    Stagnating: ITotalScoreTypes.STAGNATING,
};

const mapGrowthToTotalScore = (growth?: MarketResearchDemandAnalysisCommonModelsChangeGrowth): ITotalScoreTypes => {
    return growth ? growthToTotalScoreMap[growth] : ITotalScoreTypes.STAGNATING;
};

const createTooltipRow = (
    name: string,
    periodData: MarketResearchDemandAnalysisCalculationModelsNodeTrendPeriodData,
    color: string,
): IChartTooltipDataItemRow => {
    return {
        label: {
            name,
            color,
            date: periodData.date,
            isMonthToDate: Boolean(periodData.isMonthToDate),
        },
        volume: periodData.volume,
        monthlyChange: periodData.monthlyVolumeChange
            ? { value: periodData.monthlyVolumeChange.change, sentiment: periodData.monthlyVolumeChange.growth }
            : undefined,
        periodChange: periodData.periodVolumeChange
            ? { value: periodData.periodVolumeChange.change, sentiment: periodData.periodVolumeChange.growth }
            : undefined,
    };
};

const { currentPeriod: currentPeriodColor, previousPeriod: previousPeriodColor } = CHART_COLORS.periodOverPeriod.Common;

const selectChartData = (
    data: MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode,
): { chartData: ILineChartSeriesData[]; categories: string[] } => {
    const categories = data.trend.map((trendItem) => trendItem.currentPeriod.date);

    const regularData: PointOptionsObject[] = [];
    const previousPeriodData: PointOptionsObject[] = [];
    let monthToDateIndex: number | undefined;

    data.trend.forEach((trendItem, index) => {
        const dataPoint: PointOptionsObject = {
            x: index,
            y: trendItem.currentPeriod.volume,
            date: trendItem.currentPeriod.date,
            isMonthToDate: !!trendItem.currentPeriod.isMonthToDate,
        };

        regularData.push(dataPoint);

        if (monthToDateIndex === undefined && trendItem.currentPeriod.isMonthToDate) {
            monthToDateIndex = index;
        }

        if ("previousPeriod" in trendItem && trendItem.previousPeriod) {
            previousPeriodData.push({
                x: index,
                y: trendItem.previousPeriod.volume,
                date: trendItem.previousPeriod.date,
            });
        }
    });

    const chartDataArray: ILineChartSeriesData[] = [];
    const keywordListEntity = createKeywordListEntity(data);
    const baseId = keywordListEntity ? keywordListEntity.id : data.name;

    if (regularData.length > 0) {
        chartDataArray.push({
            id: baseId,
            name: data.name,
            data: regularData,
            isPreviousPeriod: false,
            monthToDateIndex,
        });
    }

    if (previousPeriodData.length > 0) {
        chartDataArray.push({
            id: `${baseId}-previous`,
            name: data.name,
            data: previousPeriodData,
            isPreviousPeriod: true,
        });
    }

    return { chartData: chartDataArray, categories };
};

export function selectSearchVolumeData(
    data: MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode
): TransformedData {
    const { chartData, categories } = selectChartData(data);
    const totalData =
        data.previousPeriod != null
            ? {
                  Volume: {
                      Change: data.currentPeriod?.periodVolumeChange?.change ?? 0,
                      Values: {
                          Interval: data.currentPeriod?.volume ?? 0,
                          CompareInterval: data.previousPeriod?.volume ?? 0,
                      },
                  },
                  TotalScore: mapGrowthToTotalScore(data.currentPeriod?.periodVolumeChange?.growth),
              }
            : {
                  Volume: {
                      Change: data.currentPeriod?.periodVolumeChange?.change ?? 0,
                      Value: data.currentPeriod?.volume,
                  },
                  TotalScore: mapGrowthToTotalScore(data.currentPeriod?.periodVolumeChange?.growth),
              };

    const chartTooltipData: IChartTooltipDataItem[] =
        data.trend?.map(({ currentPeriod, previousPeriod }) => {
            const { date, isMonthToDate } = currentPeriod;
            const rows: IChartTooltipDataItemRow[] = [
                createTooltipRow(data.name, currentPeriod, currentPeriodColor),
            ];

            if (previousPeriod) {
                rows.unshift(createTooltipRow(data.name, previousPeriod, previousPeriodColor));
            }

            return { name: data.name, date, rows, isMonthToDate };
        }) ?? [];

    return {
        totalData,
        chartData,
        categories,
        chartTooltipData,
    };
}
